<script setup lang="ts">
import type { CalendarDate, CalendarDraftPeriod, CalendarPeriod } from '@ui/components/calendar/picker/index'
import type {
  CalendarGetTimeSlots,
  CalendarSlotSelectOptions,
} from '@ui/components/calendar/picker/time/CalendarTimeTypes'
import CalendarActions from '@ui/components/calendar/picker/CalendarActions.vue'
import CalendarDaysPicker from '@ui/components/calendar/picker/day/CalendarDaysPicker.vue'
import { CalendarMode } from '@ui/components/calendar/picker/index'
import CalendarMonthPicker from '@ui/components/calendar/picker/month/CalendarMonthPicker.vue'
import CalendarYearPicker from '@ui/components/calendar/picker/month/CalendarYearPicker.vue'
import CalendarTimesPicker from '@ui/components/calendar/picker/time/CalendarTimesPicker.vue'
import { isDateValid } from '@ui/utils/times'
import { compareAsc, startOfDay } from 'date-fns'
import { isEqual } from 'es-toolkit'
import { computed, ref, watch } from 'vue'
import CalendarControlPanel from './CalendarControlPanel.vue'

const props = withDefaults(
  defineProps<{
    modelValue?: Date | CalendarDraftPeriod
    min?: Date
    max?: Date
    range?: boolean
    manual?: boolean
    target?: CalendarMode
    getSlots?: CalendarGetTimeSlots
    disabledDate?: (date: Date) => boolean
    today?: Date
  }>(),
  {
    range: false,
    min: undefined,
    max: undefined,
    manual: false,
    target: CalendarMode.Days,
    getSlots: () => [],
    today: () => new Date(),
  },
)

// select:date reports every selection, including an unfinished range;
// update:modelValue still reports complete ranges (or explicit confirmation in manual mode).
const emit = defineEmits<{
  'close': []
  'update:modelValue': [date: CalendarDate]
  'select:slot': [period: CalendarPeriod, options: CalendarSlotSelectOptions]
  'select:date': [date: Date | CalendarDraftPeriod]
}>()
const viewingDate = ref(props.today)
const mode = ref<CalendarMode>(props.target)

type CalendarSelection = { kind: 'date'; value: Date } | { kind: 'period'; value: CalendarDraftPeriod }

function getDate(d: unknown) {
  return d instanceof Date && isDateValid(d) ? d : undefined
}

function getInitialSelection(): CalendarSelection {
  if (props.range || props.target === CalendarMode.Times) {
    const period = props.modelValue instanceof Date ? { start: props.modelValue } : props.modelValue
    return { kind: 'period', value: { start: getDate(period?.start), end: getDate(period?.end) } }
  }
  return { kind: 'date', value: getDate(props.modelValue) || props.today }
}

const selection = ref<CalendarSelection>(getInitialSelection())
const selectedDays = computed(() => {
  const current = selection.value
  if (current.kind === 'date') {
    return current.value
  }
  return props.range ? current.value : current.value.start
})
// Slot handlers can update the period draft without committing modelValue.
const slotPeriod = computed<CalendarDraftPeriod>({
  get: () => (selection.value.kind === 'period' ? selection.value.value : {}),
  set: period => {
    selection.value = { kind: 'period', value: { start: getDate(period.start), end: getDate(period.end) } }
  },
})

watch(
  [() => props.modelValue, () => props.range, () => props.target],
  () => {
    const initial = getInitialSelection()
    selection.value = initial
    viewingDate.value = initial.kind === 'date' ? initial.value : startOfDay(initial.value.start || props.today)
  },
  { immediate: true },
)
watch(
  () => props.target,
  target => (mode.value = target),
)

const completeSelection = computed<CalendarDate | undefined>(() => {
  const current = selection.value
  if (current.kind === 'date') {
    return current.value
  }
  const { start, end } = current.value
  return start && end ? { start, end } : undefined
})
const changed = computed(() => !isEqual(completeSelection.value, props.modelValue))
const isValid = computed(() => completeSelection.value !== undefined)

function onConfirm() {
  if (completeSelection.value) {
    emit('update:modelValue', completeSelection.value)
  }
}

function onSelectDate(date: Date) {
  const current = selection.value
  if (current.kind === 'period') {
    const { start, end } = current.value
    if (!start || end) {
      selection.value = { kind: 'period', value: { start: date, end: undefined } }
      viewingDate.value = date
    } else {
      const period: CalendarPeriod = compareAsc(start, date) <= 0 ? { start, end: date } : { start: date, end: start }
      selection.value = { kind: 'period', value: period }
    }
  } else {
    selection.value = { kind: 'date', value: date }
    viewingDate.value = date
  }
  if (!props.manual) {
    onConfirm()
  }
  emit('select:date', selection.value.value)
}

function onSelectDay(d: Date) {
  if (props.target === mode.value) {
    onSelectDate(d)
    return
  }
  viewingDate.value = d
  mode.value = CalendarMode.Times
}

function onReset() {
  emit('close')
}

function onSelectTime(d: CalendarPeriod, isShift: boolean) {
  emit('select:slot', d, { range: true, isShift, viewingDate, inner: slotPeriod })
}
</script>

<template lang="pug">
.calendar-picker.flex.col-12.w-360.max-w-100p.min-h-377.p-10-20-20
  CalendarControlPanel(v-model="viewingDate" v-model:mode="mode" :today="today" :disabledDate="disabledDate" :returnMode="target")
  CalendarYearPicker(v-if="mode === CalendarMode.Years" :modelValue="viewingDate" :today="today" :disabledDate="disabledDate" @update:modelValue="viewingDate = $event; mode = CalendarMode.Months")
  CalendarMonthPicker(v-else-if="mode === CalendarMode.Months" :modelValue="viewingDate" :today="today" :disabledDate="disabledDate" @update:modelValue="viewingDate = $event; mode = CalendarMode.Days")
  CalendarDaysPicker(v-else-if="mode === CalendarMode.Days" :modelValue="selectedDays" :range="range" :today="today" :month="viewingDate" :disabledDate="disabledDate" @update:modelValue="onSelectDay")
  CalendarTimesPicker(v-else-if="mode === CalendarMode.Times" :modelValue="slotPeriod" :range="range" :day="viewingDate" :getSlots="getSlots" @select="onSelectTime")
  CalendarActions(v-if="manual" :changed="changed" :valid="isValid" @reset="onReset" @confirm="onConfirm")
</template>
